import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import Loading from "@/components-legacy/loading";
import { PremiumFeature } from "@/components-legacy/premium-feature";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import useGenerateApiKey from "@babackapp/private-api-client/client/use-generate-api-key";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import { CodeIcon, RefreshCwIcon } from "lucide-react";
import { PropsWithChildren } from "react";
import ConfirmRegenerateApiKey from "@/pages/(app)/integrations/components/api-key/confirm-regenerate-api-key.tsx";
import CopyBtn from "@/components/copy-btn";

export default function APIKeyCard() {
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();
  const { mutateAsync: generateAPIKey, isPending } = useGenerateApiKey(shop);

  const hasAtLeastAdminRights = useViewerHasAtLeastAdminRights();
  if (!hasAtLeastAdminRights) return null;

  if (!shop.plan.api?.activated) return <DoesNotHavePlan />;

  return (
    <Container>
      <CodeIcon className="size-6 text-muted-foreground" />
      <div className="flex-1">
        <CopyBtn value={shop.api.token}>
          <span className="text-muted-foreground text-sm">
            {shop.api.token}
          </span>
        </CopyBtn>
      </div>
      <ConfirmRegenerateApiKey onSubmit={() => generateAPIKey()}>
        <Button size="sm" variant="secondary">
          {isPending ? (
            <Loading className="w-4 h-4" />
          ) : (
            <>
              <RefreshCwIcon className="h-5 w-4 mr-2" />
              {t("settings.integrations.api.button")}
            </>
          )}
        </Button>
      </ConfirmRegenerateApiKey>
    </Container>
  );
}

function Container({ children }: PropsWithChildren) {
  const t = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg leading-6 font-medium text-gray-900">
          {t("settings.integrations.thirdpart.zendesk.apiKey")}
        </CardTitle>
        <CardDescription>
          {t("settings.integrations.api.apiKeyDescription")}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex gap-2 items-center">{children}</CardContent>
    </Card>
  );
}

function DoesNotHavePlan() {
  return (
    <Container>
      <div className="flex-1 h-fit">**********</div>
      <PremiumFeature />
    </Container>
  );
}
